import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { invalidateTags } from 'api/baseApi';
import { configService } from 'services/config.service';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { IDefaultAPIState } from 'types/state.interface';
import { errorNotification } from 'utils/notifications';
import {
  addEntityToState,
  deleteEntityFromState,
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
  prepEntityForAPICall,
  stateHasFetchedAllObjectsSuccess,
  upsertEntity,
} from 'utils/sliceHelpers';
import {
  type EAppId,
  EAuthStrategy,
  type IAuthConfig,
  type ISAML2Config,
  type ITEIdPConfig,
  createAuthConfig as createTAuthConfig,
} from '@timeedit/types';
import api from '../services/api.service';

type TAAuthConfigState = IDefaultAPIState<IAuthConfig>;

export const initialState: TAAuthConfigState = {
  loading: false,
  hasErrors: false,
  results: [],
  map: {},
  page: 0,
  limit: 10000,
  totalPages: 0,
};

// Slice
const slice = createSlice({
  name: 'authConfigs',
  initialState,
  reducers: {
    fetchAuthConfigsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchAuthConfigsSuccess: (
      state,
      { payload }: PayloadAction<TAAuthConfigState>
    ) => {
      stateHasFetchedAllObjectsSuccess(payload, state, createTAuthConfig);
      finishedLoadingSuccess(state);
    },
    fetchAuthConfigsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createAuthConfig: (state) => {
      addEntityToState(
        state,
        { id: 'new', name: 'New auth config', strategy: EAuthStrategy.SAML2 },
        createTAuthConfig
      );
      finishedLoadingSuccess(state);
    },
    saveAuthConfigRequest: (state) => {
      isLoadingRequest(state);
    },
    saveAuthConfigSuccess: (
      state,
      { payload }: PayloadAction<TAAuthConfigState>
    ) => {
      upsertEntity(state, payload, createTAuthConfig);
      finishedLoadingSuccess(state);
    },
    saveAuthConfigFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deleteAuthConfigRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteAuthConfigSuccess: (
      state,
      { payload: authConfigId }: PayloadAction<string>
    ) => {
      deleteEntityFromState(authConfigId, state);
      finishedLoadingSuccess(state);
    },
    deleteAuthConfigFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const authConfigsLoading = (state: TRootState) =>
  state.authConfigs.loading;
export const authConfigsSelector = (state: TRootState) =>
  state.authConfigs.results;
export const authConfigSelector =
  (id?: string) =>
  (state: TRootState): IAuthConfig | undefined =>
    id ? state.authConfigs.map[id] : undefined;
export const selectAuthStrategyForAuthConfig =
  (id: string | null | undefined) => (state: TRootState) => {
    if (!id) return undefined;
    const authConfig: IAuthConfig = state.authConfigs.map[id];
    return authConfig && authConfig.strategy ? authConfig.strategy : undefined;
  };
export const authConfigsMapSelector = (state: TRootState) =>
  state.authConfigs.map;

export const authconfigByIdSelector = createSelector(
  [
    (state: TRootState) => state.authConfigs.map,
    (_: TRootState, authConfigId: string | undefined) => authConfigId,
  ],
  (authConfigsMap, authConfigId) =>
    authConfigId ? authConfigsMap[authConfigId] : undefined
);

// Actions
export const {
  fetchAuthConfigsRequest,
  fetchAuthConfigsSuccess,
  fetchAuthConfigsFailure,
  createAuthConfig,
  deleteAuthConfigRequest,
  deleteAuthConfigFailure,
  deleteAuthConfigSuccess,
  saveAuthConfigRequest,
  saveAuthConfigSuccess,
  saveAuthConfigFailure,
} = slice.actions;

export const fetchAuthConfigs = createAppAsyncThunk(
  'authConfigs/fetchAuthConfigs',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchAuthConfigsRequest());
      const result = await api.get({
        endpoint: `/auth-configs`,
      });
      dispatch(fetchAuthConfigsSuccess(result));
    } catch (e) {
      dispatch(fetchAuthConfigsFailure());
      console.error(e);
    }
  }
);

export const saveAuthConfig = createAppAsyncThunk<void, IAuthConfig>(
  'authConfigs/saveAuthConfig',
  async (authConfig, { dispatch }) => {
    if (!authConfig) {
      errorNotification('Error saving authentication config', [
        'Auth config is undefined',
      ]);
      throw new Error('Auth config is undefined');
    }

    dispatch(saveAuthConfigRequest());
    try {
      const preppedAuthConfig = prepEntityForAPICall(
        createTAuthConfig(authConfig),
        true,
        true
      );

      if (authConfig.id === 'new') {
        const savedAuthConfig = await api.post({
          endpoint: `/auth-configs`,
          data: preppedAuthConfig,
        });
        dispatch(saveAuthConfigSuccess(savedAuthConfig));
        dispatch(deleteAuthConfigSuccess('new'));
        dispatch(invalidateTags([{ type: 'AuthConfig', id: 'LIST' }]));
      } else {
        const updatedAuthConfig = await api.patch({
          endpoint: `/auth-configs/${authConfig.id}`,
          data: preppedAuthConfig,
        });
        dispatch(saveAuthConfigSuccess(updatedAuthConfig));
        if (updatedAuthConfig?.id) {
          dispatch(
            invalidateTags([{ type: 'AuthConfig', id: updatedAuthConfig.id }])
          );
        }
      }
    } catch (error) {
      dispatch(saveAuthConfigFailure());
      errorNotification('Failed to save authentication config', [
        error instanceof Error ? error.message : 'Unknown error',
      ]);
      throw error;
    }
  }
);

export const copyAuthConfig = createAppAsyncThunk<void, IAuthConfig>(
  'authConfigs/copyAuthConfig',
  async (authConfigToCopy, { dispatch }) => {
    if (!authConfigToCopy) {
      throw new Error('Auth config to copy is undefined');
    }

    if (authConfigToCopy.strategy === EAuthStrategy.TE_IDP) {
      errorNotification('An error happened', [
        'You cannot copy a TE_IDP authentication config',
      ]);
      throw new Error('Cannot copy TE_IDP authentication config');
    }

    dispatch(saveAuthConfigRequest());
    try {
      const copiedAuthConfig = await api.post({
        endpoint: `/auth-configs/${authConfigToCopy.id}/copy`,
      });
      dispatch(saveAuthConfigSuccess(copiedAuthConfig));
      dispatch(invalidateTags([{ type: 'AuthConfig', id: 'LIST' }]));
      if (copiedAuthConfig?.id) {
        dispatch(
          invalidateTags([
            { type: 'AuthAffiliations', id: copiedAuthConfig.id },
          ])
        );
      }
    } catch (error) {
      dispatch(saveAuthConfigFailure());
      errorNotification('Failed to copy authentication config', [
        error instanceof Error ? error.message : 'Unknown error',
      ]);
      throw error;
    }
  }
);

// Thunk handles real, BE-backed deletes only. The local-only `'new'` entry is
// removed at the call site (slice action + RTK cache patch) — no API call and
// no tag invalidation are needed for it.
export const deleteAuthConfig = createAppAsyncThunk<void, string>(
  'authConfigs/deleteAuthConfig',
  async (authConfigId, { dispatch }) => {
    dispatch(deleteAuthConfigRequest());
    try {
      await api.delete({
        endpoint: `/auth-configs/${authConfigId}`,
      });
      dispatch(deleteAuthConfigSuccess(authConfigId));
      dispatch(invalidateTags([{ type: 'AuthConfig', id: authConfigId }]));
    } catch (error) {
      dispatch(deleteAuthConfigFailure());
      errorNotification('Failed to delete authentication config', [
        error instanceof Error ? error.message : 'Unknown error',
      ]);
      throw error;
    }
  }
);
